import React, { useState } from "react";
import Client from './components/Client';
import Config from './components/ClientConfig';
import { useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import IconButton from '@mui/material/IconButton';
import validator from 'validator'
import swal from 'sweetalert';
import {
  MobileInfoRow,
  MobileStandalonePage,
  MobileSurface,
} from './components/MobileStandalone';
import { useDataStore } from './dataStore';


function ResetPassword() {
  const client = React.useMemo(() => new Client("none"), []);
  const { t, languageReady } = useDataStore();
  const [showPassword, setShowPassword] = React.useState(false);
  const [resetComplete, setResetComplete] = useState(false);
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };
  const handleMouseUpPassword = (event) => {
    event.preventDefault();
  };

  const [passwordColor, setPasswordColor] = useState('');
  const [confirmColor, setConfirmColor] = useState('');
  const validateP = (value) => { 
    let newColor = '';
    if (validator.isStrongPassword(value, { 
        minLength: 8, minLowercase: 1, 
        minUppercase: 1, minNumbers: 1, minSymbols: 1 
    })) {
      newColor = '#e6ffe6';
    } else { 
      newColor = '#fbd9d3';
    } 
    setPasswordColor(newColor);
  } 

  const onChangeP = (event) => {
    const { value } = event.target;
    validateP(value);
  };

  const validateC = (value) => { 
    let newColor = '';
    if (validator.isStrongPassword(value, { 
        minLength: 8, minLowercase: 1, 
        minUppercase: 1, minNumbers: 1, minSymbols: 1 
    })) {
      newColor = '#e6ffe6';
    } else { 
      newColor = '#fbd9d3';
    } 
    setConfirmColor(newColor);
  } 

  const onChangeC = (event) => {
    const { value } = event.target;
    validateC(value);
  };

  const [urlParams]= useSearchParams();
  const email = urlParams.get('email');
  const token = urlParams.get('token');
  const hasLanguageParam = Boolean(urlParams.get('language') || urlParams.get('lang'));
  const shouldWaitForQueryLanguage = hasLanguageParam && !languageReady;
  const hasRequiredParams = Boolean(email && token);

  let queryData = { "email": email,  "forgotPasswordKey": token, "newPassword": "" };
  let url = Config.RESETPASS_API_URL;
  const passwordIsStrong = passwordColor === '#e6ffe6';
  const confirmIsStrong = confirmColor === '#e6ffe6';

  const onSubmit = (e) => {
    e.preventDefault();
    if (resetComplete) return;

    if (!hasRequiredParams) {
      swal({
        text: t("mobile.invalidResetLink"),
        icon: "error",
      });
      return;
    }

    let fpass = "";
    let cpass = "";
    if ( e.target ) {
      let objects = e.target;
      for ( let i = 0; i < objects.length; i++ ) {
        if (objects[i].id === "password") fpass = objects[i].value;
        if (objects[i].id === "passConfirm") cpass = objects[i].value;
      }
    }
    if (fpass !== cpass){
      swal({
        text: t("mobile.passwordMismatch"),
        icon: "error",
      });
    }
    else {
      queryData["newPassword"] = fpass;
      let data = client.pushData ( url, queryData );
      if (data && data.status === "OK"){
          setResetComplete(true);
        }
      else {
          swal({
            text: t("mobile.passwordNotChanged"),
            icon: "error",
          });
        }
    }
  };

  return (
    <MobileStandalonePage>
      <Box sx={{ width: "100%", pt: { xs: 5, sm: 7 } }}>
        <MobileSurface
          sx={{
            p: { xs: 2.25, sm: 2.75 },
            background: "linear-gradient(180deg, rgba(255,255,255,0.97) 0%, rgba(255,247,237,0.95) 100%)",
            border: "1px solid rgba(251,146,60,0.22)",
            boxShadow: "0 22px 54px rgba(124,45,18,0.14)",
          }}
        >
          {shouldWaitForQueryLanguage ? (
            <Box sx={{ minHeight: 180 }} />
          ) : (
            <>
              <Box sx={{ mb: 2, textAlign: "center" }}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    mx: "auto",
                    mb: 1.2,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: resetComplete ? "#0f766e" : "#9a3412",
                    background: resetComplete
                      ? "linear-gradient(135deg, #ecfdf5 0%, #a7f3d0 100%)"
                      : "linear-gradient(135deg, #fff7ed 0%, #fed7aa 72%, #fdba74 100%)",
                    border: resetComplete
                      ? "1px solid rgba(15,118,110,0.16)"
                      : "1px solid rgba(124,45,18,0.12)",
                    boxShadow: resetComplete
                      ? "0 12px 28px rgba(15,118,110,0.12)"
                      : "0 12px 28px rgba(194,65,12,0.12)",
                  }}
                >
                  {resetComplete ? <CheckCircleRoundedIcon /> : <LockResetRoundedIcon />}
                </Box>

                <Typography
                  component="h1"
                  sx={{
                    color: "#431407",
                    fontSize: 24,
                    lineHeight: 1.15,
                    fontWeight: 950,
                    letterSpacing: 0,
                  }}
                >
                  {resetComplete
                    ? t("mobile.resetPasswordSuccessTitle")
                    : t("mobile.resetPasswordTitle")}
                </Typography>

                {resetComplete || hasRequiredParams ? (
                  <Typography
                    sx={{
                      mt: 0.75,
                      color: "rgba(124,45,18,0.72)",
                      fontSize: 14,
                      lineHeight: 1.45,
                      fontWeight: 650,
                    }}
                  >
                    {resetComplete
                      ? t("mobile.resetPasswordSuccessText")
                      : t("mobile.resetPasswordIntro")}
                  </Typography>
                ) : null}
              </Box>

              {resetComplete ? (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: "8px",
                    bgcolor: "rgba(236,253,245,0.72)",
                    border: "1px solid rgba(15,118,110,0.16)",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#0f766e",
                      fontSize: 14,
                      lineHeight: 1.5,
                      fontWeight: 800,
                      textAlign: "center",
                    }}
                  >
                    {t("mobile.closeWhenFinished")}
                  </Typography>
                </Box>
              ) : hasRequiredParams ? (
                <Box
                  sx={{
                    mb: 2,
                    px: 1.25,
                    pt: 1.2,
                    borderRadius: "8px",
                    bgcolor: "rgba(255,237,213,0.46)",
                    border: "1px solid rgba(251,146,60,0.16)",
                  }}
                >
                  <MobileInfoRow label="Email">{email}</MobileInfoRow>
                </Box>
              ) : (
                <Typography
                  sx={{
                    color: "#431407",
                    fontSize: 16,
                    lineHeight: 1.5,
                    fontWeight: 750,
                    textAlign: "center",
                  }}
                >
                  {t("mobile.openResetAgain")}
                </Typography>
              )}

              {/* Form */}
              {hasRequiredParams && !resetComplete ? (
                <Box>
                  <form noValidate onSubmit={onSubmit}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      sx={{ gap: 1.5 }}
                    >
                      <FormControl fullWidth variant="outlined">
                        <InputLabel htmlFor="password">{t("mobile.newPassword")}</InputLabel>
                        <OutlinedInput
                          id="password"
                          type={showPassword ? "text" : "password"}
                          required
                          onChange={onChangeP}
                          sx={{
                            borderRadius: "8px",
                            bgcolor: passwordColor
                              ? (passwordIsStrong ? "rgba(22,101,52,0.08)" : "rgba(185,28,28,0.08)")
                              : "#fff",
                          }}
                          endAdornment={
                            <InputAdornment position="end">
                              <IconButton
                                aria-label={
                                  showPassword ? t("mobile.hidePassword") : t("mobile.showPassword")
                                }
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                onMouseUp={handleMouseUpPassword}
                                edge="end"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          }
                          label={t("mobile.newPassword")}
                        />
                      </FormControl>

                      <Typography
                        sx={{
                          alignSelf: "stretch",
                          mt: -0.75,
                          color: passwordColor
                            ? (passwordIsStrong ? "#166534" : "#991b1b")
                            : "rgba(23,23,23,0.56)",
                          fontSize: 12,
                          lineHeight: 1.35,
                          fontWeight: 700,
                        }}
                      >
                        {t("mobile.passwordHint")}
                      </Typography>

                      <FormControl fullWidth variant="outlined">
                        <InputLabel htmlFor="passConfirm">{t("mobile.repeatPassword")}</InputLabel>
                        <OutlinedInput
                          id="passConfirm"
                          type={showPassword ? "text" : "password"}
                          required
                          onChange={onChangeC}
                          sx={{
                            borderRadius: "8px",
                            bgcolor: confirmColor
                              ? (confirmIsStrong ? "rgba(22,101,52,0.08)" : "rgba(185,28,28,0.08)")
                              : "#fff",
                          }}
                          endAdornment={
                            <InputAdornment position="end">
                              <IconButton
                                aria-label={
                                  showPassword ? t("mobile.hidePassword") : t("mobile.showPassword")
                                }
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                onMouseUp={handleMouseUpPassword}
                                edge="end"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          }
                          label={t("mobile.repeatPassword")}
                        />
                      </FormControl>

                      <Button
                        sx={{
                          mt: 0.5,
                          width: "100%",
                          minHeight: 48,
                          borderRadius: "8px",
                          bgcolor: "#9a3412",
                          color: "#fff",
                          textTransform: "none",
                          fontWeight: 900,
                          boxShadow: "0 12px 24px rgba(154,52,18,0.22)",
                          "&:hover": {
                            bgcolor: "#7c2d12",
                          },
                        }}
                        type="submit"
                        fullWidth
                        variant="contained"
                      >
                        {t("mobile.changePassword")}
                      </Button>
                    </Box>
                  </form>
                </Box>
              ) : null}
            </>
          )}
        </MobileSurface>
      </Box>
    </MobileStandalonePage>
  );
}

export default ResetPassword;
